/**
 * LaGift — collection toolbar
 * ---------------------------------------------------------------------------
 * Filtering, sorting and paging without reloading the page, and the opening and
 * closing of whichever shape the filters have taken.
 *
 * The form is a real GET form and the pagination is real links, so with this
 * script gone everything still works - the `<noscript>` button appears, every
 * filter group is a real `<details>` that opens on a tap, and each change is an
 * ordinary page load. What this adds is that the browser is not asked to throw
 * the page away and build it again: the same URL is fetched, and only the two
 * parts that actually differ are swapped in.
 *
 * Two parts, not one. The results change, and so do the filters themselves -
 * counts move, options run out. Replacing only the grid would leave a sidebar
 * quietly claiming there are four of something there are now none of.
 *
 * The URL is pushed to history, so a filtered collection can be shared, opened
 * in a new tab and reached with the back button, exactly as it could when this
 * was a page load.
 *
 * Price is the exception. A number field fires `change` on every keystroke's
 * worth of thought, and submitting the page while someone is still typing "45"
 * would reload it at "4". Those wait for the field to be left alone.
 *
 * WHAT THIS SCRIPT IS NOT ALLOWED TO KNOW
 *
 * Which presentation the filters are in. There are five, chosen by two settings
 * that each only apply at one width, so the same markup is a rail on a desktop
 * and a sheet on a phone and the answer changes when the window is resized.
 * Reading the settings would be wrong and reading the viewport would be a
 * guess, so the stylesheet names the presentation in two custom properties and
 * this reads the name back. Everything conditional here goes through `panelMode`
 * and `groupMode`.
 */
/**
 * Scoped so the file can run more than once. A `<script src>` executes once per
 * tag, not once per URL, and a top-level class throws "already been declared"
 * on the second run. See CLAUDE.md.
 */
(() => {
  const TYPING_PAUSE = 700;
  const DESKTOP = '(min-width: 990px)';

  class LagiftCollectionToolbar extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onPopState = this.onPopState.bind(this);
      this.onBreakpoint = this.onBreakpoint.bind(this);

      this.form.addEventListener('change', (event) => this.onChange(event));
      this.form.addEventListener('input', (event) => this.onInput(event));
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
      this.addEventListener('click', (event) => this.onClick(event));

      /**
       * `toggle` does not bubble, so this listens in the capture phase - which
       * does reach a non-bubbling event on the way down to its target. The
       * alternative is a listener per group, re-bound after every swap.
       */
      this.addEventListener('toggle', (event) => this.onGroupToggle(event), true);

      document.addEventListener('click', this.onDocumentClick);
      window.addEventListener('popstate', this.onPopState);

      // A window dragged across the breakpoint changes which presentation is in
      // force, and the groups that a rail wants open are the ones a row of pills
      // wants closed.
      this.desktop = window.matchMedia(DESKTOP);
      this.desktop.addEventListener('change', this.onBreakpoint);

      this.syncGroups();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('popstate', this.onPopState);
      this.desktop?.removeEventListener('change', this.onBreakpoint);
      // The lock is on the document, so leaving it behind would freeze the page
      // for good if this element is ever removed while open.
      document.documentElement.classList.remove('lagift-filters-open');
      clearTimeout(this.typingTimer);
    }

    /**
     * The filters are looked up rather than kept.
     *
     * When they sit in the bar above the products they are inside the region a
     * swap replaces, so a reference taken at connect points at a detached
     * element the moment the customer ticks anything. Querying costs nothing
     * here and cannot go stale.
     */
    get filters() {
      return this.querySelector('[data-filters]');
    }

    get panel() {
      return this.querySelector('[data-panel]');
    }

    get toggle() {
      return this.querySelector('[data-panel-toggle]');
    }

    get isOpen() {
      return this.filters?.classList.contains('is-open') === true;
    }

    /** `overlay` when the panel covers the page, `inline` when it is part of it. */
    get panelMode() {
      const panel = this.panel;
      if (!panel) return 'inline';
      return getComputedStyle(panel).getPropertyValue('--lg-filters-panel').trim() || 'inline';
    }

    get isOverlay() {
      return this.panelMode === 'overlay';
    }

    /** `dropdown` when a group is a pill that opens over the page, else `stack`. */
    groupMode(group) {
      return getComputedStyle(group).getPropertyValue('--lg-filter-groups').trim() || 'stack';
    }

    /**
     * Nothing submits itself while an overlay is open.
     *
     * Auto-submitting is right for a rail or a row of pills, where the results
     * are on screen beside the control and updating them is the whole point. In
     * a sheet or a drawer it would reload the page under the customer's finger
     * and close the panel halfway through choosing, so there the footer button
     * does it.
     */
    get submitsOnChange() {
      return !(this.isOverlay && this.isOpen);
    }

    onChange(event) {
      // Price is handled on `input`, with a pause. Letting `change` through as
      // well would submit twice for one edit.
      if (event.target.type === 'number') return;
      if (!this.submitsOnChange) return;
      this.submit();
    }

    onInput(event) {
      if (event.target.type !== 'number') return;
      clearTimeout(this.typingTimer);
      if (!this.submitsOnChange) return;
      this.typingTimer = setTimeout(() => this.submit(), TYPING_PAUSE);
    }

    /** The URL the form currently describes. */
    get url() {
      // An empty field would otherwise send `filter.v.price.gte=` and pin the
      // range to zero, which is not what clearing a box means. Disabled fields
      // are left out of FormData, which is exactly the behaviour wanted.
      this.form.querySelectorAll('input[type="number"]').forEach((input) => {
        input.disabled = input.value === '';
      });

      const params = new URLSearchParams(new FormData(this.form));
      const query = params.toString();

      // Re-enable them, or a second change would silently drop a price the
      // customer can still see in the box.
      this.form.querySelectorAll('input[type="number"]').forEach((input) => {
        input.disabled = false;
      });

      return this.form.action + (query ? `?${query}` : '');
    }

    submit() {
      this.navigate(this.url);
    }

    onSubmit(event) {
      event.preventDefault();
      this.closePanel();
      this.submit();
    }

    /**
     * One listener for everything inside the section.
     *
     * Delegated rather than bound to the buttons themselves, for the same reason
     * the elements are looked up rather than kept: half of these are inside the
     * region a swap replaces, and a listener on a replaced button is a button
     * that has quietly stopped working.
     */
    onClick(event) {
      /**
       * A group the customer has opened or closed themselves is theirs from
       * then on, and `syncGroups` leaves it alone.
       *
       * Marked from the click and not from the `toggle` event, because the
       * script opens groups itself and `toggle` cannot tell the two apart: it
       * is queued rather than dispatched, so a flag set around the assignment
       * has already been cleared by the time it arrives. A click on a summary
       * is only ever a person - the keyboard fires one too.
       */
      const summary = event.target.closest('summary');
      if (summary) {
        const group = summary.closest('[data-group]');
        if (group) group.dataset.touched = 'true';
      }

      if (event.target.closest('[data-panel-toggle]')) {
        this.togglePanel();
        return;
      }

      if (event.target.closest('[data-panel-close]') || event.target.closest('[data-panel-scrim]')) {
        this.closePanel();
        return;
      }

      this.onLinkClick(event);
    }

    /**
     * Chips, "clear all" and the pagination are ordinary links, and they stay
     * ordinary links - they work with the script gone and they can be opened in
     * a new tab. Here they are simply taken the same way as everything else.
     */
    onLinkClick(event) {
      const link = event.target.closest('a');
      if (!link) return;
      if (!link.closest('.lagift-collection-toolbar__active, .lagift-pagination, .lagift-collection__panel-foot')) return;

      // Anything the customer meant to do differently is left alone.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
      if (link.target && link.target !== '_self') return;

      event.preventDefault();
      this.closePanel();
      this.navigate(link.href);
    }

    onPopState() {
      // Back and forward re-render rather than reload, and must not push the
      // entry they just arrived at back onto the stack.
      this.navigate(window.location.href, { push: false });
    }

    /**
     * Fetch a URL and swap in the parts that changed.
     *
     * Falls back to a real navigation on any failure. A collection that quietly
     * does nothing is worse than one that reloads.
     */
    async navigate(url, { push = true } = {}) {
      if (this.request) this.request.abort();
      this.request = new AbortController();

      this.setBusy(true);

      try {
        const response = await fetch(url, { signal: this.request.signal });
        if (!response.ok) throw new Error(response.status);

        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fresh = doc.getElementById(this.id) || doc.querySelector('lagift-collection-toolbar');
        if (!fresh) throw new Error('section missing');

        this.swap(fresh);

        if (push) window.history.pushState({ lagift: true }, '', url);
      } catch (error) {
        if (error.name === 'AbortError') return;
        window.location.assign(url);
        return;
      } finally {
        this.request = null;
        this.setBusy(false);
      }
    }

    /**
     * Replace the results and the filters, and put back everything the customer
     * had arranged: focus, which groups were open, and whether the panel itself
     * was open.
     *
     * Focus matters more than it looks: a customer ticking boxes with a keyboard
     * is standing on the input that just changed, and replacing the list under
     * them drops focus to the top of the document. The control is found again by
     * name and value, which survives the list being reordered or re-counted.
     *
     * The open groups matter for the same reason. Nobody ticks one box: they
     * open "Price", set it, and then reach for the next thing in the same panel.
     * Rebuilding the list from a fresh render closes every group, so the state
     * is carried across by the index the group was rendered with.
     */
    swap(fresh) {
      const active = document.activeElement;
      const focusKey = active && this.contains(active) && active.name
        ? { name: active.name, value: active.value, type: active.type }
        : null;

      const wasOpen = this.isOpen;
      const groups = {};
      this.querySelectorAll('[data-group]').forEach((group) => {
        groups[group.dataset.group] = { open: group.open, touched: group.dataset.touched === 'true' };
      });

      /**
       * Which regions to replace, which depends on where the filters were
       * rendered - and this is the one place that has to care.
       *
       * `replaceChildren` MOVES the nodes it is given, so a region nested inside
       * another cannot be taken first: emptying the fresh panel and then asking
       * the fresh `__main` for its children hands over a `__main` whose filter
       * panel is now empty. That is a filter panel with nothing in it, on every
       * tick, for the two settings that put the filters in the bar.
       *
       * So when the filters are inside `__main`, `__main` alone carries them.
       */
      const nested = !!this.querySelector('.lagift-collection__main [data-panel-scroll]');
      const regions = nested
        ? ['.lagift-collection__main']
        : ['[data-panel-scroll]', '.lagift-collection__main'];

      regions.forEach((selector) => {
        const to = this.querySelector(selector);
        const from = fresh.querySelector(selector);
        if (to && from) to.replaceChildren(...from.childNodes);
      });

      this.querySelectorAll('[data-group]').forEach((group) => {
        const was = groups[group.dataset.group];
        if (!was) return;
        group.open = was.open;
        if (was.touched) group.dataset.touched = 'true';
      });
      this.syncGroups();

      if (wasOpen) {
        // Only the class and the button, not `openPanel` - the panel was already
        // open, and re-running it would drag focus back to the close button in
        // the middle of ticking boxes.
        this.filters?.classList.add('is-open');
        this.toggle?.setAttribute('aria-expanded', 'true');
        this.querySelector('[data-panel-scrim]')?.removeAttribute('hidden');
      }

      if (focusKey) {
        const again = this.querySelector(
          `[name="${CSS.escape(focusKey.name)}"]${focusKey.type === 'checkbox' ? `[value="${CSS.escape(focusKey.value)}"]` : ''}`
        );
        if (again) {
          again.focus();
          // A number field also keeps the caret at the end, or typing a price
          // continues from the wrong place.
          if (again.type === 'number' && again.value) again.select?.();
        }
      }

      this.announce();
    }

    /** Tells a screen reader the results changed, since the page did not move. */
    announce() {
      const count = this.querySelector('.lagift-collection-toolbar__count');
      if (!count) return;
      count.setAttribute('role', 'status');
      count.setAttribute('aria-live', 'polite');
    }

    setBusy(busy) {
      this.classList.toggle('is-busy', busy);
      const main = this.querySelector('.lagift-collection__main');
      if (main) main.setAttribute('aria-busy', String(busy));
    }

    /* --- the groups ------------------------------------------------------- */

    /**
     * Which groups start open.
     *
     * Liquid renders them all closed, because that is the only state that is
     * right with no script: every group can then be opened by tapping its
     * heading. Where the presentation is a stack - a rail, a sheet, a drawer, a
     * fold-down - they should be open, and that is decided here, in the same
     * task the element first runs in, so nothing is seen to move.
     *
     * A group the customer has opened or closed themselves is left alone.
     */
    syncGroups() {
      this.querySelectorAll('[data-group]').forEach((group) => {
        if (group.dataset.touched === 'true') return;
        group.open = this.groupMode(group) !== 'dropdown';
      });
    }

    onGroupToggle(event) {
      const group = event.target.closest?.('[data-group]');
      if (!group || !this.contains(group)) return;

      // A row of pills is a row of dropdowns, and two dropdowns open over each
      // other is a mess. In a stack they are sections of one panel and there is
      // no reason to shut one to read another.
      //
      // This runs for the script's own openings as well as the customer's,
      // which is harmless: a stack never reaches the line below, and a dropdown
      // is only ever opened here by a person.
      if (!group.open || this.groupMode(group) !== 'dropdown') return;
      this.querySelectorAll('[data-group][open]').forEach((other) => {
        if (other !== group) other.open = false;
      });
    }

    /** An open dropdown closes when the customer's attention moves off it. */
    onDocumentClick(event) {
      this.querySelectorAll('[data-group][open]').forEach((group) => {
        if (group.contains(event.target)) return;
        if (this.groupMode(group) !== 'dropdown') return;
        group.open = false;
      });
    }

    /* --- the panel -------------------------------------------------------- */

    togglePanel() {
      if (this.isOpen) this.closePanel();
      else this.openPanel();
    }

    openPanel() {
      const filters = this.filters;
      if (!filters || filters.classList.contains('is-open')) return;

      filters.classList.add('is-open');
      this.toggle?.setAttribute('aria-expanded', 'true');
      this.querySelector('[data-panel-scrim]')?.removeAttribute('hidden');

      if (!this.isOverlay) return;

      // Only an overlay is a dialog. The same element is a rail, a row of pills
      // or a fold-down in the other presentations, and announcing any of those
      // as a modal would be a lie - so the roles go on at open and come off at
      // close.
      const panel = this.panel;
      panel?.setAttribute('role', 'dialog');
      panel?.setAttribute('aria-modal', 'true');
      document.documentElement.classList.add('lagift-filters-open');
      document.addEventListener('keydown', this.onKeydown);

      /**
       * Focus moves in, so the next Tab is inside the panel rather than back on
       * the page behind it.
       *
       * After a frame, not in this task. The panel is hidden with `visibility`
       * until the class lands, and nothing inside a hidden subtree can take
       * focus - called synchronously this silently does nothing and the panel
       * opens with focus still on the page behind it. Same shape as the
       * scroll-snap note in CLAUDE.md: the style has to apply first.
       */
      requestAnimationFrame(() => {
        this.querySelector('[data-panel-close]')?.focus();
      });
    }

    closePanel({ restoreFocus = true } = {}) {
      const filters = this.filters;
      if (!filters || !filters.classList.contains('is-open')) return;

      filters.classList.remove('is-open');
      this.toggle?.setAttribute('aria-expanded', 'false');
      this.releaseOverlay();

      // The scrim is hidden only once it has finished fading, or it disappears
      // instantly while the panel is still on its way out.
      const scrim = this.querySelector('[data-panel-scrim]');
      if (scrim) setTimeout(() => scrim.setAttribute('hidden', ''), 300);

      // Focus goes back where it came from, rather than to the top of the page.
      if (restoreFocus) this.toggle?.focus();
    }

    /** The page-level things an overlay takes, given back. */
    releaseOverlay() {
      const panel = this.panel;
      panel?.removeAttribute('role');
      panel?.removeAttribute('aria-modal');
      document.documentElement.classList.remove('lagift-filters-open');
      document.removeEventListener('keydown', this.onKeydown);
    }

    onKeydown(event) {
      if (event.key === 'Escape') this.closePanel();
    }

    /**
     * The window crossed the breakpoint, so the presentation changed under us.
     *
     * Two things have to be put right. The groups may now want the opposite
     * state - a rail wants them open, a row of pills wants them closed - and an
     * overlay that was open may no longer be an overlay, in which case the page
     * is still locked and the panel is now simply part of the layout. A locked
     * page with no visible panel is unscrollable for no reason the customer can
     * see.
     */
    onBreakpoint() {
      this.syncGroups();
      if (!this.isOverlay) this.releaseOverlay();
    }
  }

  if (!customElements.get('lagift-collection-toolbar')) {
    customElements.define('lagift-collection-toolbar', LagiftCollectionToolbar);
  }
})();
